/**
 * useActiveModules — UPT-2069 (L1) : l'UNIQUE source de vérité web des modules ouverts (actifs ou en essai) du
 * locataire courant, branchée sur l'auth-context servi par le serveur (`moduleEntitlements`, vocabulaire
 * `moduleScopes`). Toute garde (route, menu, champ inter-modules `moduleGate`, lien vers un module) passe par ce hook
 * ou, hors composant, par `isModuleOpen` (uptk/core/modules/activeModules.ts), qui lit la même source.
 *
 * Premier rendu, avant l'auth-context : `modules` reprend le snapshot `localStorage` comme simple CACHE d'affichage
 * et `ready` vaut `false` ; un snapshot vide ou absent donne « aucun module ouvert ». Les appelants qui redirigent
 * attendent `ready`.
 *
 * Historique : UPT-1853 (alias de tableaux de bord de module, DashboardSwitcher). Réactif : abonnement au magasin
 * d'auth-context, sans requête ; recharger l'auth-context (après une action de la Marketplace) suffit.
 */

import { useEffect, useState } from 'react';
import {
  getAuthContextState,
  subscribeAuthContextState,
} from '@/uptk/core/api/auth_context_store';
import { loadModuleEntitlementSnapshot } from '@/uptk/core/entitlements/module_entitlement_storage';
import {
  isModuleCodeActive as isCodeActiveIn,
  normalizeModuleKey,
  readActiveModules,
  type ActiveModuleRef,
} from '@/uptk/core/modules/activeModules';

export type { ActiveModuleRef } from '@/uptk/core/modules/activeModules';

export type UseActiveModulesResult = {
  modules: ActiveModuleRef[];
  /** False until the auth context has loaded at least once — callers that gate a redirect on `modules` should wait
   * for `ready` first, otherwise an active module would bounce away during the brief window before the auth context
   * arrives. */
  ready: boolean;
};

/**
 * True when `code` designates one of the active modules: real code (`finance-addon-pmo`), canonical code, alias
 * (`crisis-management` ⇄ `crisis`) or domain (`pmo`, `iso22301`) — vocabulary served by the server.
 */
export function isModuleCodeActive(modules: ActiveModuleRef[], code: string): boolean {
  return isCodeActiveIn(modules, code);
}

/** Cache de premier rendu : les modules que le snapshot déclare actifs (code seul, sans vocabulaire). */
const snapshotModules = (): ActiveModuleRef[] => {
  const snapshot = loadModuleEntitlementSnapshot();
  if (!snapshot) return [];
  return Object.entries(snapshot.entitlements ?? {})
    .filter(([, record]) => Boolean(record?.isActive))
    .map(([key, record]) => {
      const code = normalizeModuleKey(record?.moduleCode || key);
      return { code, label: code, status: 'ACTIVE' as const, canonicalCode: code, domains: [], aliases: [] };
    })
    .filter((module) => module.code.length > 0);
};

const computeState = (): UseActiveModulesResult => {
  const context = getAuthContextState();
  if (context == null) {
    return { modules: snapshotModules(), ready: false };
  }
  return { modules: readActiveModules(context), ready: true };
};

export function useActiveModules(): UseActiveModulesResult {
  const [state, setState] = useState<UseActiveModulesResult>(computeState);

  useEffect(() => {
    // L'auth-context a pu arriver entre le premier rendu et l'abonnement.
    setState(computeState());
    return subscribeAuthContextState(() => {
      setState(computeState());
    });
  }, []);

  return state;
}

export default useActiveModules;
